<template>
  <div class="nav-bar">
    <div class="logo">
      <img src="@/assets/logo.png" alt="Logo" class="logo-img">
      <span class="logo-text">{{ isAdmin ? '管理系统' : '用户系统' }}</span>
    </div>
    
    <!-- 桌面版菜单 -->
    <el-menu
      :default-active="activeIndex"
      class="el-menu-horizontal desktop-menu"
      mode="horizontal"
      background-color="#304156"
      text-color="#bfcbd9"
      active-text-color="#409EFF"
      :ellipsis="false"
      router
    >
      <!-- 统一菜单项 -->
      <el-menu-item index="/order">
        <el-icon><Document /></el-icon>
        <span>订单管理</span>
      </el-menu-item>

      <el-menu-item :index="isAdmin ? '/admin/users' : '/user/info'">
        <el-icon><User /></el-icon>
        <span>{{ isAdmin ? '用户管理' : '个人信息' }}</span>
      </el-menu-item>
      
      <el-menu-item index="/mail">
        <el-icon><Van /></el-icon>
        <span>快递查询</span>
      </el-menu-item>

      <el-menu-item v-if="isAdmin" index="/admin/analyze">
        <el-icon><pie-chart /></el-icon>
        <span>用户状况分析</span>
      </el-menu-item>

      <el-menu-item v-if="isAdmin" index="/ad">
        <el-icon><Setting /></el-icon>
        <span>广告控制面板</span>
      </el-menu-item>

      <el-menu-item v-if="isAdmin" index="/admin/audit">
        <el-icon><Tickets /></el-icon>
        <span>审计中心</span>
      </el-menu-item>

    </el-menu>

    <div
      class="mailing-notice"
      role="button"
      tabindex="0"
      title="点击复制镜框邮寄地址"
      @click="copyMailingAddress"
      @keydown.enter="copyMailingAddress"
      @keydown.space.prevent="copyMailingAddress"
    >
      <div class="mailing-notice-track">
        <span>{{ mailingAddress }}</span>
        <span aria-hidden="true">{{ mailingAddress }}</span>
      </div>
    </div>

    <div class="user-actions">
      <el-dropdown trigger="click" @command="handleCommand" v-if="userInfo.username" class="user-actions-dropdown">
        <span class="user-dropdown">
          <el-badge
            v-if="isAdmin && pendingStats.total > 0"
            :value="pendingStats.total"
            type="danger"
            class="pending-badge-dot"
          >
            <span class="user-name">{{ userInfo.username }}</span>
          </el-badge>
          <span v-else class="user-name">{{ userInfo.username }}</span>
          <el-icon class="el-icon--right"><arrow-down /></el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="profile">个人信息</el-dropdown-item>
            <el-dropdown-item
              v-if="isAdmin"
              command="pending"
              class="pending-dropdown-item"
            >
              待处理订单
              <el-badge v-if="pendingStats.total > 0" :value="pendingStats.total" type="danger" class="inner-badge"/>
            </el-dropdown-item>
            <el-dropdown-item command="logout">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {Document, User, ArrowDown, Van, PieChart, Setting, Tickets} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useHotspotStore } from '@/stores/hotspotStore'
import { redirectToLogin } from '@/utils/authRedirect'

const route = useRoute()
const router = useRouter()
const activeIndex = ref('')
const mailingAddress = '镜框邮寄地址：广州市花都区新华街曙光路61号粤旺里第四层 豪赋13326457402'

const copyMailingAddress = async () => {
  try {
    await navigator.clipboard.writeText(mailingAddress)
    ElMessage.success('镜框邮寄地址已复制')
  } catch {
    const textarea = document.createElement('textarea')
    textarea.value = mailingAddress
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()

    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    ElMessage[copied ? 'success' : 'error'](copied ? '镜框邮寄地址已复制' : '复制失败，请手动复制')
  }
}

// 从localStorage获取用户信息
const userInfo = computed(() => {
  return JSON.parse(localStorage.getItem('userInfo') || '{}')
})

// 从localStorage获取用户角色信息
const isAdmin = computed(() => {
  return userInfo.value.role === 'ADMIN'
})

// 判断是否在订单相关路由
// const isOrderRoute = computed(() => {
//   const currentPath = route.path
//   const orderRelatedRoutes = [
//     '/admin/orders',
//     '/user/orders',
//     '/supermaster',
//     '/astigmatism',
//     '/bulkorders',
//     '/orderprocessing',
//     '/model',
//     '/unit',
//     '/ad'
//   ]
//   return orderRelatedRoutes.some(route => currentPath.includes(route)) ||
//          currentPath === '/order' ||
//          currentPath.startsWith('/order/')
// })

// 根据用户角色设置默认活动页面
watch(() => route.path, (newPath) => {
  activeIndex.value = newPath
}, { immediate: true })

const hotspotStore = useHotspotStore()
const pendingStats = computed(() => hotspotStore.stats)

onMounted(() => {
  if (isAdmin.value) hotspotStore.startPolling()
  window.addEventListener('hotspot-refresh', hotspotStore.fetchStats)
})

onBeforeUnmount(() => {
  hotspotStore.stopPolling()
  window.removeEventListener('hotspot-refresh', hotspotStore.fetchStats)
})

// 处理移动端菜单命令
// const handleMobileMenuCommand = (command) => {
//   if (command === 'logout') {
//     handleLogout()
//   } else if (command === 'pending') {
//     // 弹窗展示详情
//     const { regular, fuxiangsan, bulk, total } = pendingStats.value
//     ElMessage.info(`待处理订单共 ${total} 条，其中：\n超主点订单 ${regular} 条\n其他订单 ${fuxiangsan} 条\n批量订单 ${bulk} 条`)
//     router.push('/orderprocessing')
//   } else {
//     router.push(command)
//   }
// }

// 处理用户下拉菜单命令
const handleCommand = (command) => {
  if (command === 'profile') {
    router.push('/user/info')
  } else if (command === 'logout') {
    handleLogout()
  } else if (command === 'pending') {
    // 弹窗展示详情
    const { regular, fuxiangsan, bulk, total } = pendingStats.value
    ElMessage.info(`待处理订单共 ${total} 条，其中：\n超主点订单 ${regular} 条\n其他订单 ${fuxiangsan} 条\n批量订单 ${bulk} 条`)
    router.push('/orderprocessing')
  }
}

// 处理退出登录
const handleLogout = () => {
  redirectToLogin(router, {
    message: '已退出登录',
    messageType: 'success'
  })
}
</script>

<style scoped>
.nav-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #304156;
  display: flex;
  align-items: center;
  height: 60px; /* 确保导航栏有固定高度 */
  padding: 0 15px;
}

/* 确保用户下拉菜单在导航栏之上 */
:deep(.el-dropdown-menu) {
  z-index: 1100 !important;
}

/* 确保下拉菜单容器也有足够高的z-index */
:deep(.el-popper.is-dropdown) {
  z-index: 1100 !important;
}

/* 移动端菜单下拉按钮 */
.mobile-menu-dropdown {
  display: none; /* 默认隐藏，仅在移动端显示 */
  margin-left: auto;
  margin-right: 10px;
}

.mobile-menu-trigger {
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 24px;
  padding: 8px;
}

.mobile-menu-trigger:hover {
  color: #409EFF;
}

/* 移动端下拉菜单项样式 */
:deep(.mobile-menu-dropdown .el-dropdown-menu__item) {
  display: flex;
  align-items: center;
  gap: 8px;
}

:deep(.mobile-menu-dropdown .el-dropdown-menu__item .el-icon) {
  margin-right: 0;
}

.logo {
  display: flex;
  align-items: center;
  margin-right: 45px;
  height: 60px; /* 或者根据实际情况调整 */
  box-sizing: border-box; /* 确保边框和内边距计算在内 */
  border-bottom: none; /* 确保没有底部边框 */
}

.logo-img {
  width: 60px;
  height: 32px; /* 图片自身高度，通过 align-items: center 使其在垂直方向居中 */
  margin-right: 10px;
  margin-left: 20px;
}

.logo-text {
  color: #fff;
  font-size: 16px;
  font-weight: bold;
}

.el-menu-horizontal {
  border-bottom: none !important; /* 强制移除底部边框 */
  background-color: transparent !important;
  flex:1;
  height: 60px; /* 确保菜单容器高度与导航栏高度一致 */
}

/* 禁用Element Plus菜单的省略号显示 */
.el-menu-horizontal :deep(.el-menu-overflow) {
  display: none !important;
}

.el-menu-horizontal :deep(.el-menu-overflow-trigger) {
  display: none !important;
}

.el-menu-item {
  height: 60px !important; /* 强制设置高度，覆盖Element Plus默认样式 */
  line-height: 60px !important; /* 强制设置行高，确保文字垂直居中 */
  position: relative;
  padding: 0 20px !important; /* 确保内边距一致 */
  box-sizing: border-box; /* 确保padding计算在内 */
  border-bottom: none !important; /* 确保没有底部边框 */
}

.el-menu-item:hover {
  background-color: #263445 !important;
}

.el-menu-item.is-active {
  &::after {
    content: ''; /* 伪元素必须要有 content 属性 */
    position: absolute; /* 绝对定位 */
    left: 0; /* 定位在左边 */
    bottom: 0; /* 定位在底部 */
    width: 100%; /* 宽度与父元素一致 */
    height: 2px; /* 下划线高度，可以适当调高，比如 2px，看起来更明显 */
    background-color: #409EFF; /* 下划线颜色 */
    z-index: 1; /* 确保伪元素在上面 */
  }
}

.el-menu-item .el-icon {
  margin-right: 8px;
  font-size: 18px;
}

.user-actions {
  margin-right: 20px;
  margin-left: auto; /* 推送到最右侧 */
}

.mailing-notice {
  width: min(620px, 50vw);
  flex: 0 1 620px;
  margin-right: 24px;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  line-height: 34px;
  border-radius: 4px;
  outline: none;
  -webkit-mask-image: linear-gradient(to right, transparent, #000 18px, #000 calc(100% - 36px), transparent);
  mask-image: linear-gradient(to right, transparent, #000 18px, #000 calc(100% - 36px), transparent);
}

.mailing-notice:hover,
.mailing-notice:focus-visible {
  background-color: rgba(255, 255, 255, 0.08);
}

.mailing-notice-track {
  display: flex;
  width: max-content;
  animation: mailing-notice-scroll 18s linear infinite;
}

.mailing-notice-track span {
  flex: none;
  padding-right: 80px;
}

.mailing-notice:hover .mailing-notice-track,
.mailing-notice:focus-visible .mailing-notice-track {
  animation-play-state: paused;
}

@keyframes mailing-notice-scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}


.user-dropdown {
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
}

/* 中等屏幕尺寸优化 (1024px - 769px) */
@media screen and (max-width: 1024px)  {
  .el-menu-item {
    padding: 0 13px !important; /* 减少内边距 */
    font-size: 14px; /* 缩小字体 */
  }


  .el-menu-item .el-icon {
    margin-right: 6px;
    font-size: 16px; /* 缩小图标 */
  }
  

  
  .logo-text {
    font-size: 13px; /* 稍微缩小logo文字 */
  }
}

@media screen and (max-width: 910px) {
  .logo{
    margin-right: 20px;
  }
  .logo-img {
    width: 50px;
    height: 32px;
    margin-left: 2px;
    margin-right: 5px;
  }

  /* 进一步缩小菜单项 */
  .el-menu-item {
    padding: 0 10px !important;
    font-size: 12px;
  }
  
  .el-menu-item .el-icon {
    margin-right: 3px;
    font-size: 15px;
  }
}

/* 响应式布局 */
@media screen and (max-width: 768px) {
  
  .logo-text {
    display: none;
  }

  .logo {
    margin-right: 20px;
  }
  
  .logo-img {
    margin-left: 0;
  }
  
  /* 隐藏桌面菜单 */
  .desktop-menu {
    display: none;
  }

  .mailing-notice {
    width: auto;
    flex: 1 1 auto;
    margin: 0 12px 0 0;
    font-size: 13px;
  }
  
  /* 调整用户操作区域 */
  .user-actions {
    right: 10px;
  }
  
  .user-dropdown span:not(.el-icon) {
    display: none; /* 隐藏用户名，只保留图标 */
  }
}

@media (prefers-reduced-motion: reduce) {
  .mailing-notice-track {
    animation-duration: 36s;
  }
}

.pending-badge-dot {
  margin-right: 6px;
}

.inner-badge {
  margin-left: 8px;
}
</style>
